import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface PushNotificationOptions {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  requireInteraction?: boolean;
  data?: any;
}

export const usePushNotifications = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Check if push notifications are supported
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);

    if (supported) {
      setPermission(Notification.permission);
      checkSubscription();
    }
  }, []);

  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const requestPermission = async (): Promise<boolean> => {
    if (!isSupported) {
      toast({
        title: "Not Supported",
        description: "Push notifications are not supported in this browser",
        variant: "destructive"
      });
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result === 'granted') {
        toast({
          title: "Notifications Enabled",
          description: "You'll now receive push notifications for important updates"
        });
        return true;
      } else if (result === 'denied') {
        toast({
          title: "Notifications Blocked",
          description: "To enable notifications: Click the lock icon in your address bar, then allow notifications and refresh the page",
          variant: "destructive"
        });
        return false;
      } else {
        toast({
          title: "Notifications Not Enabled",
          description: "Please allow notifications to receive important updates",
          variant: "default"
        });
        return false;
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
      toast({
        title: "Error",
        description: "Failed to request notification permission",
        variant: "destructive"
      });
      return false;
    }
  };

  const subscribe = async (): Promise<boolean> => {
    if (permission !== 'granted') {
      const granted = await requestPermission();
      if (!granted) return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      
      // Check if already subscribed
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        setIsSubscribed(true);
        return true;
      }

      // Create new subscription
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(getVapidPublicKey())
      });

      // Store subscription in database
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from('device_tokens').upsert({
          user_id: user.id,
          token: JSON.stringify(subscription),
          updated_at: new Date().toISOString()
        });
      }

      setIsSubscribed(true);
      toast({
        title: "Subscribed",
        description: "Successfully subscribed to push notifications"
      });
      return true;
    } catch (error) {
      console.error('Error subscribing:', error);
      toast({
        title: "Subscription Failed",
        description: "Failed to subscribe to push notifications",
        variant: "destructive"
      });
      return false;
    }
  };

  const unsubscribe = async (): Promise<boolean> => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        
        // Remove from database
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase.from('device_tokens')
            .delete()
            .eq('user_id', user.id)
            .eq('token', JSON.stringify(subscription));
        }
      }

      setIsSubscribed(false);
      toast({
        title: "Unsubscribed",
        description: "Successfully unsubscribed from push notifications"
      });
      return true;
    } catch (error) {
      console.error('Error unsubscribing:', error);
      toast({
        title: "Unsubscribe Failed",
        description: "Failed to unsubscribe from push notifications",
        variant: "destructive"
      });
      return false;
    }
  };

  const sendNotification = (options: PushNotificationOptions) => {
    if (permission === 'granted') {
      new Notification(options.title, {
        body: options.body,
        icon: options.icon || '/ovit-logo.png',
        badge: options.badge || '/ovit-logo.png',
        tag: options.tag,
        requireInteraction: options.requireInteraction,
        data: options.data
      });
    }
  };

  return {
    isSupported,
    permission,
    isSubscribed,
    requestPermission,
    subscribe,
    unsubscribe,
    sendNotification
  };
};

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray as Uint8Array<ArrayBuffer>;
}

// VAPID public key - should be stored in environment variables in production
function getVapidPublicKey(): string {
  return 'BK8Z5SZ4zLlKZzKA1jJj5YpJ5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj5zJj';
}